const CANVAS_PRINT_TIERS = [
  [0.2, 2400],
  [0.35, 2000],
  [0.5, 1600],
  [0.8, 1400],
  [1.3, 1300],
  [Infinity, 1250],
];

const PAPER_PRINT_TIERS = [
  [0.2, 2300],
  [0.25, 2000],
  [0.3, 2000],
  [0.35, 1715],
  [0.4, 1625],
  [0.45, 1600],
  [0.5, 1600],
  [0.8, 1500],
  [1.3, 1400],
  [Infinity, 1350],
];

const BARYTA_PRINT_TIERS = [
  [0.1, 3000],
  [0.15, 3333],
  [0.2, 3500],
  [0.25, 3800],
  [0.4, 3500],
  [0.5, 3200],
  [0.6, 3000],
  [0.7, 2857],
  [0.8, 2750],
  [0.9, 2670],
  [1.0, 2600],
  [1.1, 2545],
  [1.2, 2500],
  [1.4, 2450],
  [1.5, 2400],
  [1.6, 2375],
  [1.8, 2200],
  [2.0, 2500],
];

const STRETCH_TIERS = [
  [0.3, 1250],
  [0.4, 1350],
  [0.5, 1450],
  [0.6, 1550],
  [0.7, 1650],
  [0.8, 1750],
  [0.9, 1850],
  [1.0, 1950],
  [1.25, 2050],
  [1.5, 2500],
  [2.0, 3000],
];

const BLACK_FRAME_TIERS = [
  [0.34, 1200],
  [0.4, 1400],
  [0.5, 1500],
  [0.7, 1600],
  [0.79, 1800],
  [0.9, 2000],
  [0.99, 2150],
  [1.1, 2250],
  [1.2, 2500],
  [1.3, 2700],
  [1.4, 3000],
  [1.5, 3500],
  [1.6, 4000],
  [1.7, 4500],
  [1.8, 5000],
  [1.9, 5500],
  [2.0, 6000],
];

const WOOD_FRAME_TIERS = [
  [0.34, 1750],
  [0.4, 1850],
  [0.45, 2050],
  [0.6, 2250],
  [0.7, 2450],
  [0.79, 2600],
  [0.89, 2750],
  [0.99, 2900],
  [1.09, 2950],
  [1.19, 3250],
  [1.29, 3450],
  [1.39, 3750],
  [1.49, 4250],
  [1.59, 4750],
  [1.69, 5250],
  [1.79, 5750],
  [1.89, 6250],
  [2.0, 6750],
];

const PROFILE_BOUNDS = {
  canvas: {
    minWidth: 50,
    maxWidth: 110,
    minHeight: 50,
    maxHeight: 300,
  },
  paper: {
    minWidth: 30,
    maxWidth: 110,
    minHeight: 30,
    maxHeight: 200,
  },
  baryta: {
    minWidth: 30,
    maxWidth: 110,
    minHeight: 30,
    maxHeight: 200,
  },
};

class AePrintCalculator extends HTMLElement {
  constructor() {
    super();

    this.moneyFormatter = new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: this.dataset.currency || 'NOK',
      maximumFractionDigits: 0,
    });

    this.priceBankMap = new Map();
    this.priceBankProducts = [];
    this.loadedPriceBankHandles = new Set();
    this.loadingPriceBankHandles = new Map();
    this.priceBankStatus = 'idle';
    this.isAdding = false;
    this.currentQuote = null;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-ae-calculator-form]');
    if (!this.form) return;

    this.profile = ['paper', 'baryta'].includes(this.dataset.profile) ? this.dataset.profile : 'canvas';
    this.widthInput = this.querySelector('[data-ae-width]');
    this.heightInput = this.querySelector('[data-ae-height]');
    this.quantityInput = this.querySelector('[data-ae-quantity]');
    this.certificateInput = this.querySelector('[data-ae-certificate]');
    this.deckleInput = this.querySelector('[data-ae-deckle]');
    this.uvInput = this.querySelector('[data-ae-uv]');
    this.stretchInput = this.querySelector('[data-ae-stretch]');
    this.frameInputs = Array.from(this.querySelectorAll('[data-ae-frame]'));
    this.frameOptions = this.querySelector('[data-ae-frame-options]');
    this.stretchOptionPrice = this.querySelector('[data-ae-stretch-option-price]');
    this.contactNotice = this.querySelector('[data-ae-contact-notice]');
    this.addButton = this.querySelector('[data-ae-add-to-cart]');
    this.statusElement = this.querySelector('[data-ae-status]');
    this.fieldErrors = {
      width: this.querySelector('[data-ae-width-error]'),
      height: this.querySelector('[data-ae-height-error]'),
      quantity: this.querySelector('[data-ae-quantity-error]'),
    };

    this.summary = {
      print: this.querySelector('[data-ae-print-price]'),
      certificate: this.querySelector('[data-ae-certificate-price]'),
      deckle: this.querySelector('[data-ae-deckle-price]'),
      uv: this.querySelector('[data-ae-uv-price]'),
      stretch: this.querySelector('[data-ae-stretch-price]'),
      frame: this.querySelector('[data-ae-frame-price]'),
      subtotal: this.querySelector('[data-ae-subtotal]'),
      discount: this.querySelector('[data-ae-discount]'),
      total: this.querySelector('[data-ae-total]'),
    };

    this.form.addEventListener('input', () => this.update());
    this.form.addEventListener('change', () => this.update());
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    this.update();
    this.priceBankPromise = this.loadPriceBank();
  }

  update() {
    const values = this.getValues();
    const quote = this.calculateQuote(values);
    this.currentQuote = quote;

    this.updateValidationUI(quote.validation);
    this.updateOptionAvailability(quote);
    this.renderQuote(quote);
  }

  getValues() {
    return {
      widthRaw: this.widthInput?.value ?? '',
      heightRaw: this.heightInput?.value ?? '',
      quantityRaw: this.quantityInput?.value ?? '',
      width: Number(this.widthInput?.value || 0),
      height: Number(this.heightInput?.value || 0),
      quantity: Number(this.quantityInput?.value || 1),
      certificate: Boolean(this.certificateInput?.checked),
      deckle: Boolean(this.deckleInput?.checked),
      uv: Boolean(this.uvInput?.checked),
      stretch: Boolean(this.stretchInput?.checked),
      frame: this.getSelectedFrame(),
    };
  }

  calculateQuote(values) {
    const bounds = this.getBounds();
    const validation = this.getValidationState(values, bounds);
    const width = this.clamp(values.width, bounds.minWidth, bounds.maxWidth);
    const height = this.clamp(values.height, bounds.minHeight, bounds.maxHeight);
    const quantity = this.clamp(Math.round(values.quantity), 1, 50);
    const area = (width * height) / 10000;
    const priceGroup = this.getPriceGroup(area, Math.max(width, height));
    const printRate = this.getPrintRate(priceGroup);
    const contactRequired = printRate === null;

    const certificatePrice = values.certificate ? 60 : 0;
    const deckleSelected = this.profile === 'paper' && values.deckle;
    const decklePrice = deckleSelected ? 130 : 0;
    const uvSelected = ['paper', 'baryta'].includes(this.profile) && values.uv;
    const uvPrice = uvSelected ? 100 : 0;
    const canvasOptionsAvailable = this.profile === 'canvas' && area <= 2;
    const stretchSelected = this.profile === 'canvas' && values.stretch && canvasOptionsAvailable;
    const stretchPrice = stretchSelected ? this.matchTier(priceGroup, STRETCH_TIERS) : 0;

    const frame = stretchSelected ? values.frame : 'none';
    const framePrice = this.getFramePrice(priceGroup, frame);
    const printPrice = contactRequired ? 0 : priceGroup * printRate;
    const unitPrice = contactRequired ? 0 : printPrice + certificatePrice + decklePrice + uvPrice + stretchPrice + framePrice;
    const subtotal = unitPrice * quantity;
    const discountFactor = this.getDiscountFactor(quantity);
    const total = Math.round(subtotal * discountFactor);

    return {
      width,
      height,
      quantity,
      area,
      priceGroup,
      profile: this.profile,
      validation,
      contactRequired,
      certificate: values.certificate,
      deckle: deckleSelected,
      uv: uvSelected,
      canvasOptionsAvailable,
      stretch: stretchSelected,
      frame,
      printPrice,
      certificatePrice,
      decklePrice,
      uvPrice,
      stretchPrice,
      framePrice,
      unitPrice,
      subtotal,
      discountFactor,
      total,
      totalCents: total * 100,
    };
  }

  async loadPriceBank() {
    const configUrl = this.dataset.priceBankConfigUrl;
    if (!configUrl) {
      this.priceBankStatus = 'error';
      this.setStatus('Prisbank-konfig mangler.');
      this.renderQuote(this.currentQuote);
      return;
    }

    this.priceBankStatus = 'loading';
    this.renderQuote(this.currentQuote);

    try {
      const configResponse = await fetch(configUrl, { headers: { Accept: 'application/json' } });
      if (!configResponse.ok) throw new Error('Kunne ikke hente prisbank-konfig.');

      const config = await configResponse.json();
      const configuredProducts = Array.isArray(config.products) ? config.products : [];
      const handles = Array.isArray(config.handles) ? config.handles : [];

      this.priceBankProducts = configuredProducts.length > 0
        ? configuredProducts
            .filter((product) => product?.handle)
            .map((product) => ({
              handle: product.handle,
              minPriceCents: Number(product.minPriceCents),
              maxPriceCents: Number(product.maxPriceCents),
            }))
        : handles.map((handle) => ({
            handle,
            minPriceCents: 0,
            maxPriceCents: Infinity,
          }));

      if (this.priceBankProducts.length === 0) throw new Error('Prisbank-konfig har ingen handles.');

      this.priceBankStatus = 'ready';
      this.setStatus('');
    } catch (error) {
      console.error(error);
      this.priceBankStatus = 'error';
      this.setStatus('Prisbanken er ikke tilgjengelig ennå. Importer prisbankproduktene først.');
    }

    this.renderQuote(this.currentQuote);
  }

  async fetchPriceBankProduct(handle) {
    const rootUrl = this.dataset.rootUrl || '/';
    const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    const response = await fetch(`${normalizedRoot}products/${handle}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Fant ikke prisbankprodukt: ${handle}`);
    }

    return response.json();
  }

  async loadPriceBankProduct(handle) {
    if (this.loadedPriceBankHandles.has(handle)) return;
    if (this.loadingPriceBankHandles.has(handle)) {
      await this.loadingPriceBankHandles.get(handle);
      return;
    }

    const promise = this.fetchPriceBankProduct(handle)
      .then((product) => {
        for (const variant of product.variants || []) {
          const priceCents = this.normalizeVariantPriceToCents(variant.price);
          if (Number.isFinite(priceCents) && variant.id) {
            this.priceBankMap.set(priceCents, String(variant.id));
          }
        }

        this.loadedPriceBankHandles.add(handle);
      })
      .finally(() => {
        this.loadingPriceBankHandles.delete(handle);
      });

    this.loadingPriceBankHandles.set(handle, promise);
    await promise;
  }

  getPriceBankProductForPrice(totalCents) {
    return this.priceBankProducts.find((product) => {
      return totalCents >= product.minPriceCents && totalCents <= product.maxPriceCents;
    });
  }

  async getVariantIdForQuote(quote) {
    const existingVariantId = this.priceBankMap.get(quote.totalCents);
    if (existingVariantId) return existingVariantId;

    const product = this.getPriceBankProductForPrice(quote.totalCents);
    if (!product) return null;

    await this.loadPriceBankProduct(product.handle);
    return this.priceBankMap.get(quote.totalCents) || null;
  }

  normalizeVariantPriceToCents(rawPrice) {
    if (typeof rawPrice === 'string' && rawPrice.includes('.')) {
      return Math.round(Number.parseFloat(rawPrice) * 100);
    }

    const value = Number(rawPrice);
    return Number.isFinite(value) ? Math.round(value) : Number.NaN;
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.update();

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    if (this.isAdding) return;

    if (this.priceBankStatus === 'loading') {
      this.setStatus('Prisbanken lastes fortsatt. Prøv igjen om et øyeblikk.');
      return;
    }

    if (this.priceBankStatus !== 'ready') {
      this.setStatus('Prisbanken er ikke klar ennå.');
      return;
    }

    const quote = this.currentQuote || this.calculateQuote(this.getValues());
    if (quote.contactRequired) {
      this.setStatus('Kontakt oss for pris på dette formatet.');
      return;
    }

    if (!this.priceBankMap.has(quote.totalCents)) {
      this.setStatus('Henter riktig prisvariant...');
    }

    const variantId = await this.getVariantIdForQuote(quote);

    if (!variantId) {
      this.setStatus(`Fant ikke prisbankvariant for ${this.formatMoney(quote.total)}.`);
      return;
    }

    this.isAdding = true;
    this.renderQuote(quote);
    this.setStatus('Legger i handlekurven...');

    try {
      const addResponse = await this.addToCart(variantId, quote);
      const cart = await this.fetchCart();
      this.dispatchCartUpdate(cart, addResponse, variantId);
      this.setStatus('Lagt i handlekurven.');
    } catch (error) {
      console.error(error);
      this.setStatus(error.message || 'Kunne ikke legge varen i handlekurven.');
    } finally {
      this.isAdding = false;
      this.renderQuote(this.currentQuote);
    }
  }

  async addToCart(variantId, quote) {
    const sectionIds = this.getCartSectionIds();
    const payload = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: this.getCartProperties(quote),
        },
      ],
    };

    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
    }

    const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Shopify avviste varen.');
    }

    return data;
  }

  async fetchCart() {
    const response = await fetch(this.dataset.cartUrl || '/cart.js', {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;
    return response.json();
  }

  dispatchCartUpdate(cart, addResponse, variantId) {
    this.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'ae-print-calculator',
          data: {
            source: 'ae-print-calculator',
            itemCount: cart?.item_count ?? 1,
            variantId: String(variantId),
            sections: addResponse.sections,
          },
        },
      })
    );
  }

  getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    return sectionIds;
  }

  getCartProperties(quote) {
    const properties = {
      Størrelse: `${quote.width} x ${quote.height} cm`,
      'Antall trykk': String(quote.quantity),
      _ae_calculator: quote.profile,
      _ae_original_product_id: this.dataset.productId || '',
      _ae_product_title: this.dataset.productTitle || '',
      _ae_product_url: this.dataset.productUrl || '',
      _ae_area: `${quote.area.toFixed(4)} m2`,
      _ae_price_group: `${quote.priceGroup.toFixed(2)} m2`,
      _ae_total_cents: String(quote.totalCents),
    };

    if (quote.certificate) properties.Sertifikat = 'Ja';
    if (quote.deckle) properties['Deckle edge'] = 'Ja';
    if (quote.uv) properties['UV-spraylakk'] = 'Ja';
    if (quote.stretch) properties['Oppspent på blindramme'] = 'Ja';
    if (quote.frame !== 'none') properties.Ramme = this.getFrameLabel(quote.frame);
    if (quote.discountFactor < 1) properties.Rabatt = this.formatDiscount(quote.discountFactor);

    return properties;
  }

  getPriceGroup(area, longestSide) {
    const step = longestSide > 120 ? 0.1 : 0.05;
    return Number((Math.ceil(area / step) * step).toFixed(2));
  }

  getDiscountFactor(quantity) {
    if (quantity >= 30) return 0.8;
    if (quantity >= 20) return 0.85;
    if (quantity >= 15) return 0.9;
    if (quantity >= 10) return 0.95;
    return 1;
  }

  getPrintRate(priceGroup) {
    const tiers = this.profile === 'baryta'
      ? BARYTA_PRINT_TIERS
      : this.profile === 'paper'
        ? PAPER_PRINT_TIERS
        : CANVAS_PRINT_TIERS;
    const match = tiers.find(([upperBound]) => priceGroup <= upperBound + Number.EPSILON);
    return match ? match[1] : null;
  }

  getValidationState(values, bounds) {
    const fields = {
      width: this.getNumberError(values.widthRaw, values.width, bounds.minWidth, bounds.maxWidth, 'Bredde', 'cm'),
      height: this.getNumberError(values.heightRaw, values.height, bounds.minHeight, bounds.maxHeight, 'Høyde', 'cm'),
      quantity: this.getNumberError(values.quantityRaw, values.quantity, 1, 50, 'Antall', ''),
    };

    return {
      fields,
      isValid: Object.values(fields).every((message) => !message),
    };
  }

  getNumberError(rawValue, value, min, max, label, unit) {
    const suffix = unit ? ` ${unit}` : '';

    if (String(rawValue).trim() === '') return `${label} må fylles ut.`;
    if (!Number.isFinite(value)) return `${label} må være et tall.`;
    if (value < min) return `${label} må være minst ${min}${suffix}.`;
    if (value > max) return `${label} kan maks være ${max}${suffix}.`;
    if (!Number.isInteger(value)) return `${label} må oppgis i hele ${unit || 'tall'}.`;

    return '';
  }

  getFramePrice(area, frame) {
    if (frame === 'black') {
      return this.matchTier(area, BLACK_FRAME_TIERS);
    }

    if (frame === 'oak' || frame === 'walnut') {
      return this.matchTier(area, WOOD_FRAME_TIERS);
    }

    return 0;
  }

  getStretchPreviewPrice(area) {
    return this.matchTier(area, STRETCH_TIERS);
  }

  updateOptionAvailability(quote) {
    if (this.contactNotice) {
      const shouldShowNotice = quote.validation.isValid && (quote.contactRequired || (this.profile === 'canvas' && !quote.canvasOptionsAvailable));
      this.contactNotice.hidden = !shouldShowNotice;
    }

    if (!this.stretchInput) return;

    const shouldDisableCanvasOptions = !quote.canvasOptionsAvailable;
    this.stretchInput.disabled = shouldDisableCanvasOptions;

    if (shouldDisableCanvasOptions) {
      this.stretchInput.checked = false;
    }

    const shouldDisableFrameOptions = shouldDisableCanvasOptions || !this.stretchInput.checked;

    if (this.frameInputs.length > 0) {
      if (shouldDisableFrameOptions) this.setSelectedFrame('none');

      for (const input of this.frameInputs) {
        input.disabled = shouldDisableFrameOptions;
      }
    }

    if (this.frameOptions) {
      this.frameOptions.toggleAttribute('data-disabled', shouldDisableFrameOptions);
    }

  }

  renderQuote(quote) {
    if (!quote) return;

    const hasValidationError = !quote.validation.isValid;
    const priceText = hasValidationError ? '-' : quote.contactRequired ? 'Kontakt oss' : this.formatMoney(quote.printPrice);
    const subtotalText = hasValidationError ? '-' : quote.contactRequired ? 'Kontakt oss' : this.formatMoney(quote.subtotal);
    const totalText = hasValidationError ? 'Rett opp målene' : quote.contactRequired ? 'Kontakt oss' : this.formatMoney(quote.total);

    this.setText(this.summary.print, priceText);
    this.setText(this.summary.certificate, this.formatMoney(quote.certificatePrice));
    this.setText(this.summary.deckle, this.formatMoney(quote.decklePrice));
    this.setText(this.summary.uv, this.formatMoney(quote.uvPrice));
    this.setText(this.summary.stretch, quote.stretch ? this.formatMoney(quote.stretchPrice) : this.formatMoney(0));
    this.setText(
      this.stretchOptionPrice,
      quote.canvasOptionsAvailable ? this.formatMoney(quote.stretchPrice || this.getStretchPreviewPrice(quote.priceGroup)) : 'Kontakt oss'
    );
    this.setText(this.summary.frame, quote.frame !== 'none' ? this.formatMoney(quote.framePrice) : this.formatMoney(0));
    this.setText(this.summary.subtotal, subtotalText);
    this.setText(this.summary.discount, this.formatDiscount(quote.discountFactor));
    this.setText(this.summary.total, totalText);
    this.toggleSummaryRow('print', !hasValidationError);
    this.toggleSummaryRow('certificate', quote.certificate);
    this.toggleSummaryRow('deckle', quote.deckle);
    this.toggleSummaryRow('uv', quote.uv);
    this.toggleSummaryRow('stretch', quote.stretch);
    this.toggleSummaryRow('frame', quote.frame !== 'none' && quote.framePrice > 0);
    this.toggleSummaryRow('subtotal', !hasValidationError && !quote.contactRequired && quote.discountFactor < 1);
    this.toggleSummaryRow('discount', !hasValidationError && !quote.contactRequired && quote.discountFactor < 1);

    this.updateAddButtonState(quote);
  }

  updateAddButtonState(quote) {
    if (!this.addButton) return;

    if (this.isAdding) {
      this.addButton.disabled = true;
      this.addButton.textContent = 'Legger i handlekurven...';
      return;
    }

    if (!quote.validation.isValid) {
      this.addButton.disabled = true;
      this.addButton.textContent = 'Rett opp målene';
      return;
    }

    if (this.priceBankStatus === 'loading') {
      this.addButton.disabled = true;
      this.addButton.textContent = 'Laster prisbank...';
      return;
    }

    if (this.priceBankStatus === 'error') {
      this.addButton.disabled = true;
      this.addButton.textContent = 'Importer prisbank først';
      return;
    }

    if (this.priceBankStatus !== 'ready') {
      this.addButton.disabled = true;
      this.addButton.textContent = 'Laster kalkulator...';
      return;
    }

    if (quote.contactRequired) {
      this.addButton.disabled = true;
      this.addButton.textContent = 'Kontakt oss for pris';
      return;
    }

    const variantId = this.priceBankMap.get(quote.totalCents);
    const product = this.getPriceBankProductForPrice(quote.totalCents);
    this.addButton.disabled = !variantId && !product;
    this.addButton.textContent = variantId || product ? 'Legg i handlekurv' : 'Pris mangler i prisbank';
  }

  getBounds() {
    const fallback = PROFILE_BOUNDS[this.profile] || PROFILE_BOUNDS.canvas;

    return {
      minWidth: this.getNumberData('minWidth', fallback.minWidth),
      maxWidth: this.getNumberData('maxWidth', fallback.maxWidth),
      minHeight: this.getNumberData('minHeight', fallback.minHeight),
      maxHeight: this.getNumberData('maxHeight', fallback.maxHeight),
    };
  }

  getNumberData(name, fallback) {
    const value = Number(this.dataset[name]);
    return Number.isFinite(value) ? value : fallback;
  }

  matchTier(value, tiers) {
    const match = tiers.find(([upperBound]) => value <= upperBound + Number.EPSILON);
    return match ? match[1] : 0;
  }

  clamp(value, min, max) {
    if (Number.isNaN(value)) return min;
    return Math.min(Math.max(value, min), max);
  }

  formatMoney(value) {
    return this.moneyFormatter.format(Math.round(value));
  }

  formatDiscount(discountFactor) {
    const discount = Math.round((1 - discountFactor) * 100);
    return discount > 0 ? `-${discount}%` : '0%';
  }

  getFrameLabel(frame) {
    if (frame === 'black') return 'Sort';
    if (frame === 'oak') return 'Eik';
    if (frame === 'walnut') return 'Valnøtt';
    return 'Ingen';
  }

  getSelectedFrame() {
    const selectedFrame = this.frameInputs.find((input) => input.checked);
    return selectedFrame?.value || 'none';
  }

  setSelectedFrame(frame) {
    for (const input of this.frameInputs) {
      input.checked = input.value === frame;
    }
  }

  toggleSummaryRow(rowName, shouldShow) {
    const row = this.querySelector(`[data-ae-summary-row="${rowName}"]`);
    if (row) row.hidden = !shouldShow;
  }

  updateValidationUI(validation) {
    if (!validation) return;

    this.setFieldError(this.widthInput, this.fieldErrors.width, validation.fields.width);
    this.setFieldError(this.heightInput, this.fieldErrors.height, validation.fields.height);
    this.setFieldError(this.quantityInput, this.fieldErrors.quantity, validation.fields.quantity);
  }

  setFieldError(input, errorElement, message) {
    if (input) {
      input.setCustomValidity(message || '');
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    if (errorElement) {
      errorElement.hidden = !message;
      errorElement.textContent = message || '';
    }
  }

  setText(element, value) {
    if (element) element.textContent = value;
  }

  setStatus(message) {
    if (this.statusElement) this.statusElement.textContent = message;
  }
}

if (!customElements.get('ae-print-calculator')) {
  customElements.define('ae-print-calculator', AePrintCalculator);
}
